/** Private preview rasters only, partitioned by account/project and content version. */
import { SUPABASE_URL } from '../../utils/supabase/url';
const DATABASE = 'quantis-document-previews-v1';
const STORE = 'previews';
const MAX_ENTRIES = 200;
const MAX_BYTES = 32 * 1024 * 1024;
const MAX_ENTRY_BYTES = 1024 * 1024;
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
interface Entry { key: string; identity: string; blob: Blob; usedAt: number; createdAt: number }
export interface ThumbnailScope { userId: string; projectId: string; fileId: string; modifiedTime: string; revision?: string }
let generation = 0;

export function thumbnailIdentity(scope: ThumbnailScope): string {
  return JSON.stringify([SUPABASE_URL, scope.userId, scope.projectId, scope.fileId]);
}
export function persistentThumbnailKey(scope: ThumbnailScope): string | null {
  if (!scope.userId || !scope.projectId || !scope.modifiedTime) return null;
  return JSON.stringify([thumbnailIdentity(scope), scope.modifiedTime, scope.revision || '']);
}
function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: 'key' });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('Preview cache blocked'));
  });
}
async function transaction<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore, resolve: (value: T) => void) => void): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      let result: T;
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
      action(tx.objectStore(STORE), (value) => { result = value; });
    });
  } finally { db.close(); }
}
export async function clearDocumentThumbnailStore(): Promise<void> {
  generation += 1;
  try { await transaction<void>('readwrite', (store, resolve) => { store.clear(); resolve(); }); } catch { /* Private mode/storage unavailable. */ }
}
if (typeof window !== 'undefined') window.addEventListener('quantis:auth-cleared', () => { void clearDocumentThumbnailStore(); });

export async function getPersistentDocumentThumbnail(scope: ThumbnailScope, load: () => Promise<Blob>): Promise<Blob> {
  const key = persistentThumbnailKey(scope);
  const started = generation;
  if (key) {
    try {
      const cached = await transaction<Entry | undefined>('readwrite', (store, resolve) => {
        const request = store.get(key);
        request.onsuccess = () => {
          const entry = request.result as Entry | undefined;
          if (entry && Date.now() - entry.createdAt <= MAX_AGE) {
            entry.usedAt = Date.now(); store.put(entry); resolve(entry);
          } else { if (entry) store.delete(key); resolve(undefined); }
        };
      });
      if (started !== generation) throw new Error('Preview session ended');
      if (cached) return cached.blob;
    } catch (error) { if (started !== generation) throw error; }
  }
  const blob = await load();
  if (started !== generation) throw new Error('Preview session ended');
  if (key && blob.size <= MAX_ENTRY_BYTES) {
    try {
      await transaction<void>('readwrite', (store, resolve) => {
        const request = store.getAll();
        request.onsuccess = () => {
          if (started !== generation) { resolve(); return; }
          const identity = thumbnailIdentity(scope);
          const now = Date.now();
          const entries = (request.result as Entry[]).filter((entry) => {
            if (entry.identity === identity || now - entry.createdAt > MAX_AGE) { store.delete(entry.key); return false; }
            return true;
          }).sort((a, b) => a.usedAt - b.usedAt);
          let bytes = entries.reduce((sum, entry) => sum + entry.blob.size, 0) + blob.size;
          while (entries.length >= MAX_ENTRIES || bytes > MAX_BYTES) {
            const oldest = entries.shift(); if (!oldest) break;
            bytes -= oldest.blob.size; store.delete(oldest.key);
          }
          store.put({ key, identity, blob, createdAt: now, usedAt: now } satisfies Entry); resolve();
        };
      });
    } catch { /* Quota/private mode: previews continue without persistence. */ }
  }
  return blob;
}
